import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '../lib/utils';

/**
 * The one donor button (CON-11): the widget's and checkout's shadcn buttons,
 * ThemeButton's base, the donor portal's, and the donate call-to-action (`cta`).
 * Every variant and size below is the donor redesign standard (CON-514); the
 * rule set is SPEC.md §C in docs/initiatives/new/donor-experience-redesign.
 */

// ── The donor redesign standard (CON-514, SPEC §C) ─────────────────────────
//
// One look for every donor button, whatever family a call site names: 48 tall
// (15/22 semibold) or 36 tall (14/20 semibold), radius 12, no shadow, the 2px
// accent focus outline offset 2, and one disabled level (the 60% opacity dial).
// Brand paint reads the runtime ROLE tokens BrandTokenProvider writes on the
// scope element (brandTokens.js); each var() falls back to the no-brand value
// (ink fill, white label) for a surface with no provider.
//
// The `family` axis survives because call sites pass it, and because one
// behaviour still differs: the shadcn families keep `disabled:pointer-events-none`;
// the CTA does not (its not-allowed cursor is the affordance).
// Every class below is a LITERAL string: Tailwind only generates a class it can
// read in the source, so a class assembled from an interpolated constant never
// reaches the stylesheet.
const BASE =
  'inline-flex items-center justify-center gap-2 rounded-[var(--cs-control-radius)] font-semibold ' +
  'transition-colors duration-[var(--cs-motion-instant)] ' +
  'focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)] ' +
  'disabled:cursor-not-allowed disabled:opacity-[var(--cs-opacity-disabled)] [&_svg]:shrink-0 [&_svg]:size-[var(--cs-icon)]';

const SHADCN_FAMILY = 'whitespace-nowrap disabled:pointer-events-none [&_svg]:pointer-events-none';

const PRIMARY =
  'border-0 bg-[color:var(--cs-brand-action,var(--cs-text-primary))] ' +
  'text-[color:var(--cs-brand-foreground,var(--cs-surface-0))] ' +
  'hover:bg-[color:var(--cs-brand-action-hover,var(--cs-text-primary))] ' +
  'active:bg-[color:var(--cs-brand-action-pressed,var(--cs-text-primary))] ' +
  'disabled:hover:bg-[color:var(--cs-brand-action,var(--cs-text-primary))]';

const SECONDARY =
  'border border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)] text-[color:var(--cs-text-primary)] ' +
  'hover:border-[color:var(--cs-border-control-hover)] hover:bg-[color:var(--cs-surface-1)] ' +
  'active:bg-[color:var(--cs-surface-sunken)] [&_svg]:text-[color:var(--cs-text-secondary)] ' +
  'disabled:hover:border-[color:var(--cs-border-control)] disabled:hover:bg-[color:var(--cs-surface-0)]';

const buttonVariantsBase = cva(BASE, {
  variants: {
    // Declared first so cva emits it before `variant`.
    family: {
      widget: SHADCN_FAMILY,
      checkout: SHADCN_FAMILY,
      portal: SHADCN_FAMILY,
      cta: '',
    },
    variant: {
      // Primary: the brand action fill and its label (SPEC §C hover/pressed).
      default: PRIMARY,
      primary: PRIMARY,
      'primary-dynamic': `font-sans ${PRIMARY}`,
      // The donate call-to-action. Full width; wraps rather than truncating.
      cta: `flex w-full whitespace-normal ${PRIMARY}`,
      // Secondary / outlined: surface, 1px edge, ink label.
      secondary: SECONDARY,
      outline: SECONDARY,
      // Was the brand-tint fill; the redesign has no tinted button, so it is
      // the outlined secondary (SPEC §C "Secondary/outlined buttons").
      brandSecondary: SECONDARY,
      // Destructive actions only (Cancel subscription, Remove card): error fill,
      // white label (SPEC §E12).
      destructive:
        'border-0 bg-[color:var(--cs-status-error-text)] text-[color:var(--cs-surface-0)]',
      // Transparent icon buttons inside title strips, modals and sheets.
      ghost:
        'bg-transparent text-[color:var(--cs-text-secondary)] hover:bg-[color:var(--cs-surface-1)] ' +
        'active:bg-[color:var(--cs-surface-sunken)]',
      // Links: brand text, always underlined, the size of the text around them.
      link:
        'h-auto p-0 font-medium text-[color:var(--cs-brand-text,var(--cs-text-primary))] underline decoration-1 ' +
        '[text-underline-position:from-font] text-[length:inherit] leading-[inherit]',
      custom: '',
    },
    size: {
      // 48 — primary and secondary buttons, every full-width action.
      default:
        'h-[var(--cs-size-control)] px-5 text-[length:var(--cs-button-font-size)] leading-[var(--cs-button-line-height)]',
      lg: 'h-[var(--cs-size-control)] px-5 text-[length:var(--cs-button-font-size)] leading-[var(--cs-button-line-height)]',
      // 36 — inline / small buttons (card, table and header actions).
      sm: 'h-[var(--cs-size-button-sm)] px-4 text-[length:var(--cs-button-sm-font-size)] leading-[var(--cs-button-sm-line-height)]',
      // 36 square — Close, Share, Open in Maps. The 20 glyph is set by the call
      // site with `[&_svg]:size-[var(--cs-icon-lg)]` on the BUTTON (a class on
      // the svg loses to the base's descendant 16 rule). Not set here, because
      // the cart stepper glyphs borrow this size.
      icon: 'h-[var(--cs-size-button-sm)] w-[var(--cs-size-button-sm)] p-0',
      // Checkout's three names are all the 48 full-width action now (the old
      // 44 / 38 / 40 heights are retired, SPEC §C).
      checkout:
        'min-h-[var(--cs-size-control)] px-5 py-0 text-[length:var(--cs-button-font-size)] leading-[var(--cs-button-line-height)]',
      payment:
        'h-[var(--cs-size-control)] px-5 text-[length:var(--cs-button-font-size)] leading-[var(--cs-button-line-height)]',
      submit:
        'h-[var(--cs-size-control)] px-5 text-[length:var(--cs-button-font-size)] leading-[var(--cs-button-line-height)]',
      // The CTA wraps on narrow widths, so its 48 is a floor. Its type comes from
      // `.dynamic-button-text` (donor-type-ramp.css), which reads the same dial.
      cta: 'min-h-[var(--cs-size-control)] px-5 py-0 text-[length:var(--cs-button-font-size)] leading-[var(--cs-button-line-height)]',
      // Adds nothing. For a call site that owns its own box.
      none: '',
    },
  },
  defaultVariants: {
    family: 'widget',
    variant: 'default',
    size: 'default',
  },
});

// A link takes the size of the text around it (SPEC §E15), so it never gets the
// default 48 box: with no explicit size, `variant: 'link'` resolves to `none`.
const buttonVariants = (props = {}) =>
  buttonVariantsBase(
    props.variant === 'link' && props.size == null ? { ...props, size: 'none' } : props,
  );

const Button = React.forwardRef(
  ({ className, family, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ family, variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
